import { predicates } from './predicates.js';
import type { AssessmentSubject, MeasurementSession, Observation } from './subject.js';
import type { ProtocolSpec, Rule, Severity, Side, Trait } from './types.js';

/**
 * Engine de validacao. Funcoes puras: mesma entrada, mesma saida, sem I/O.
 * Roda identico no tablet (offline, antes de fechar a ficha) e no servidor
 * (job noturno de qualidade). Duas implementacoes divergiriam; uma so, nao.
 */

export interface Finding {
  ruleId: string;
  severity: Severity;
  message: string;
  /** Traits envolvidos — o app usa para levar o foco ao campo certo. */
  traits: string[];
  /** Em qual sessao de medicao o problema apareceu (indice em subject.sessions). */
  sessionIndex: number | null;
  side: Side | null;
  /** O que foi observado, para exibir junto da mensagem. */
  observed: string | null;
}

export interface ValidationResult {
  findings: Finding[];
  errors: Finding[];
  warnings: Finding[];
  /** Falso enquanto houver `erro`. A ficha nao fecha. */
  canClose: boolean;
}

const byCode = (traits: Trait[]) => new Map(traits.map((t) => [t.code, t]));

/** Lados que um trait exige. Nao-lateralizado usa uma unica observacao com side 'NA'. */
const expectedSides = (t: Trait): Side[] => (t.sided ? ['E', 'D'] : ['NA']);

const pick = (session: MeasurementSession, trait: string, side: Side): Observation | undefined =>
  session.observations.find((o) => o.trait === trait && o.side === side);

/** Valor de um trait nao-lateralizado dentro de uma sessao. */
const valueOf = (session: MeasurementSession, trait: string): number | null =>
  pick(session, trait, 'NA')?.value ?? null;

const round = (n: number, decimals = 2) => Number(n.toFixed(decimals));

export function validateAssessment(
  spec: ProtocolSpec,
  subject: AssessmentSubject,
): ValidationResult {
  const traits = byCode(spec.traits);
  const findings: Finding[] = [];

  const add = (f: Finding) => findings.push(f);

  // As sessoes de campo sao as que carregam a obrigatoriedade. Uma sessao de
  // metodo 'foto' e preenchida depois, no escritorio, e so traz os angulos.
  const fieldSessions = subject.sessions
    .map((session, index) => ({ session, index }))
    .filter(({ session }) => session.method === 'campo');

  for (const rule of spec.rules) {
    switch (rule.kind) {
      case 'required': {
        const trait = traits.get(rule.trait);
        if (!trait) break;
        if (subject.level < rule.when.levelAtLeast) break;
        if (rule.when.sexIn && !rule.when.sexIn.includes(subject.sex)) break;
        if (rule.when.fitnessApto && subject.management.fitness !== 'apto') break;

        for (const { session, index } of fieldSessions) {
          for (const side of expectedSides(trait)) {
            const o = pick(session, trait.code, side);
            const preenchido = o != null && (o.value != null || o.notApplicable);
            if (!preenchido) {
              add({
                ruleId: rule.id,
                severity: rule.severity,
                message: rule.message,
                traits: [trait.code],
                sessionIndex: index,
                side: trait.sided ? side : null,
                observed: null,
              });
            }
          }
        }
        break;
      }

      case 'range': {
        const trait = traits.get(rule.trait);
        if (!trait) break;
        for (const { session, index } of fieldSessions) {
          for (const o of session.observations) {
            if (o.trait !== rule.trait || o.value == null) continue;
            if (rule.scope?.sex && !rule.scope.sex.includes(subject.sex)) continue;
            if (o.value < rule.min || o.value > rule.max) {
              add({
                ruleId: rule.id,
                severity: rule.severity,
                message: rule.message,
                traits: [rule.trait],
                sessionIndex: index,
                side: o.side,
                observed: `${o.value} ${trait.unit}`,
              });
            }
          }
        }
        break;
      }

      case 'linearIdentity': {
        for (const { session, index } of fieldSessions) {
          const parcelas = rule.terms.map((t) => ({ trait: t.trait, coef: t.coef, value: valueOf(session, t.trait) }));
          const alvo = valueOf(session, rule.equals);
          // Regra so dispara com todos os termos preenchidos. Campo vazio e
          // problema de `required`, nao de consistencia.
          if (alvo == null || parcelas.some((p) => p.value == null)) continue;

          const soma = parcelas.reduce((acc, p) => acc + p.coef * (p.value as number), 0);
          const desvio = soma - alvo;
          if (Math.abs(desvio) > rule.tolerance) {
            add({
              ruleId: rule.id,
              severity: rule.severity,
              message: rule.message,
              traits: [...rule.terms.map((t) => t.trait), rule.equals],
              sessionIndex: index,
              side: null,
              observed: `${round(soma, 1)} vs ${alvo} ${rule.unit} (desvio ${round(desvio, 1)} ${rule.unit})`,
            });
          }
        }
        break;
      }

      case 'ratio': {
        for (const { session, index } of fieldSessions) {
          const num = valueOf(session, rule.num);
          const den = valueOf(session, rule.den);
          if (num == null || den == null || den === 0) continue;
          const r = num / den;
          if (r < rule.min || r > rule.max) {
            add({
              ruleId: rule.id,
              severity: rule.severity,
              message: rule.message,
              traits: [rule.num, rule.den],
              sessionIndex: index,
              side: null,
              observed: `${rule.num}/${rule.den} = ${round(r, 3)}`,
            });
          }
        }
        break;
      }

      case 'predicate': {
        const detalhe = predicates[rule.name](subject);
        if (detalhe != null) {
          add({
            ruleId: rule.id,
            severity: rule.severity,
            message: rule.message,
            traits: [],
            sessionIndex: null,
            side: null,
            observed: detalhe,
          });
        }
        break;
      }
    }
  }

  const ordem: Record<Severity, number> = { erro: 0, aviso: 1 };
  findings.sort((a, b) => ordem[a.severity] - ordem[b.severity] || a.ruleId.localeCompare(b.ruleId));

  const errors = findings.filter((f) => f.severity === 'erro');
  const warnings = findings.filter((f) => f.severity === 'aviso');

  return { findings, errors, warnings, canClose: errors.length === 0 };
}

/**
 * Triagem da visita inteira — a tela de Fechamento do dia. E a funcionalidade
 * que a folha 1 da ficha chama de "validacao automatica rodada antes de sair do
 * haras", e a que evita a viagem de volta.
 */
export interface SheetResult<TRef> {
  ref: TRef;
  result: ValidationResult;
}

export function validateVisit<TRef>(
  spec: ProtocolSpec,
  sheets: Array<{ ref: TRef; subject: AssessmentSubject }>,
): { sheets: SheetResult<TRef>[]; canCloseVisit: boolean; errorCount: number; warningCount: number } {
  const results = sheets.map(({ ref, subject }) => ({ ref, result: validateAssessment(spec, subject) }));
  const errorCount = results.reduce((n, r) => n + r.result.errors.length, 0);
  const warningCount = results.reduce((n, r) => n + r.result.warnings.length, 0);
  return { sheets: results, canCloseVisit: errorCount === 0, errorCount, warningCount };
}

export type { Rule };
